import React, { useEffect, useState, useRef } from 'react';

import { Bell, AlertTriangle } from 'lucide-react';

import NotificationPanel from '@/components/shared/NotificationPanel';
import { getAuthToken } from '../../utils/auth';
import { useSocketEmit, useSocketEvent, useSocketStatus } from '../../hooks/useSocket';

type Props = {
  onClick?: () => void;
};

// ✅ ENTERPRISE: System notification type for Gmail/service errors
type SystemNotification = {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  source: string;
  timestamp: Date;
};

export default function NotificationBell({ onClick }: Props) {
  const [count, setCount] = useState<number>(0);
  const [useFallback, setUseFallback] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const fallbackErrorCountRef = useRef(0);
  const FALLBACK_MAX_ERRORS = 3;
  const socketStatus = useSocketStatus();
  const emitSocket = useSocketEmit();
  const realtimeDisabled = socketStatus === 'disabled';

  // ✅ ENTERPRISE: System notifications (Gmail errors, etc.)
  const [systemNotifications, setSystemNotifications] = useState<SystemNotification[]>([]);
  const [showSystemAlert, setShowSystemAlert] = useState(false);

  // Fetch count function - defined early so it can be used in useEffect hooks
  const fetchCount = React.useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch('/api/notifications/unread-count', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCount(data.count || 0);
      }
    } catch (err) {
      // Silent fail
    }
  }, []);

  // Fetch initial unread count on mount
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    if (socketStatus === 'disabled') {
      setUseFallback(true);
      return;
    }
    if (socketStatus === 'connected') {
      setUseFallback(false);
    }
  }, [socketStatus]);

  // ✅ ENTERPRISE: Listen for Gmail/service errors
  useEffect(() => {
    const handleGmailError = (event: CustomEvent) => {
      const { message, type, retryCount, maxRetries } = event.detail;

      console.log(`🔔 [Notifications] Gmail event: ${message}`);

      // Add to system notifications
      const notification: SystemNotification = {
        id: `gmail_${Date.now()}`,
        message,
        type,
        source: 'Gmail',
        timestamp: new Date(),
      };

      setSystemNotifications(prev => {
        // Keep only last 5 system notifications
        const updated = [notification, ...prev].slice(0, 5);
        return updated;
      });

      // Show alert indicator on bell
      if (type === 'error') {
        setShowSystemAlert(true);
      }
    };

    window.addEventListener('gmail:error', handleGmailError as EventListener);

    return () => {
      window.removeEventListener('gmail:error', handleGmailError as EventListener);
    };
  }, []);

  // Polling Fallback (disabled by default to avoid unnecessary API calls)
  useEffect(() => {
    if (!useFallback || realtimeDisabled) return;

    // Simple polling fallback - check for notifications every 30 seconds
    console.log('🔄 [Notifications] Using polling mode');

    const checkNotifications = async () => {
      try {
        const response = await fetch('/api/notifications/unread-count', {
          headers: {
            Authorization: `Bearer ${getAuthToken() || ''}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCount(data.count || 0);
          fallbackErrorCountRef.current = 0;
        }
      } catch (err) {
        // Silent fail - notifications are not critical
        if (process.env.NODE_ENV === 'development') {
          console.log('🔕 [Notifications] Polling failed (expected if endpoint not implemented)');
        }
        fallbackErrorCountRef.current += 1;
        if (fallbackErrorCountRef.current >= FALLBACK_MAX_ERRORS) {
          console.warn('⚠️ [Notifications] Polling failed repeatedly, pausing polling');
          setUseFallback(false);
        }
      }
    };

    // Initial check
    checkNotifications();

    // Poll every 30 seconds
    const interval = setInterval(checkNotifications, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [useFallback, realtimeDisabled]);

  useEffect(() => {
    if (socketStatus !== 'connected') return;
    emitSocket('subscribe', 'notifications');
    emitSocket('subscribe', 'user-notifications');
    return () => {
      emitSocket('unsubscribe', 'notifications');
      emitSocket('unsubscribe', 'user-notifications');
    };
  }, [socketStatus, emitSocket]);

  useSocketEvent('notify', () => {
    fetchCount();
  });

  useSocketEvent('notification', () => {
    fetchCount();
  });

  useSocketEvent('notification-count-updated', () => {
    fetchCount();
  });

  const handleBellClick = () => {
    setPanelOpen(!panelOpen);
    onClick?.();
  };

  const handleMarkAsRead = (id: string) => {
    // Refresh count when notification is marked as read
    fetchCount();
  };

  const handleMarkAllAsRead = () => {
    // Refresh count when all notifications are marked as read
    fetchCount();
  };

  return (
    <div className="relative">
      <button
        ref={bellRef}
        onClick={handleBellClick}
        className="relative rounded-2xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2.5 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-neutral-700 transition-colors"
        aria-label="notifications"
        title={
          realtimeDisabled
            ? 'Real-time notifications unavailable. Retrying shortly.'
            : undefined
        }
      >
        <Bell className="h-4 w-4 text-slate-700 dark:text-neutral-300" />

        {/* Regular notification count */}
        {count > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center justify-center border-2 border-white dark:border-neutral-800">
            {count > 99 ? '99+' : count}
          </span>
        )}

        {/* ✅ ENTERPRISE: System alert indicator (Gmail errors, etc.) */}
        {showSystemAlert && (
          <span className="absolute -top-1 -left-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center border-2 border-white dark:border-neutral-800 animate-pulse">
            <AlertTriangle className="h-2.5 w-2.5 text-white" />
          </span>
        )}

        {realtimeDisabled && (
          <span className="absolute -bottom-1 -left-1 h-4 w-4 rounded-full bg-amber-500 flex items-center justify-center border-2 border-white dark:border-neutral-800">
            <AlertTriangle className="h-2.5 w-2.5 text-white" />
          </span>
        )}
      </button>

      {/* ✅ ENTERPRISE: System notification tooltip on hover */}
      {showSystemAlert && systemNotifications.length > 0 && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-3 shadow-lg z-50 pointer-events-none">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">System Alert</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {systemNotifications[0]?.message}
              </p>
              <button
                className="text-xs text-red-500 hover:text-red-700 mt-2 pointer-events-auto"
                onClick={() => setShowSystemAlert(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        systemNotifications={systemNotifications}
        onDismissSystemNotification={id => {
          setSystemNotifications(prev => prev.filter(n => n.id !== id));
          if (systemNotifications.length <= 1) setShowSystemAlert(false);
        }}
      />
    </div>
  );
}
